import type { ChangeEvent, ReactNode } from "react";
import { useRef, useState } from "react";
import type { ShipmentDocument } from "api";
import { Modal } from "components/ui/Modal";
import { DOCUMENT_ACCEPT, DOCUMENT_MAX_BYTES, DOCUMENT_TOO_LARGE } from "./docs.model";

type DocumentActions = {
  /** Opens the file picker; the chosen file goes to that container, or to the shipment for null. */
  chooseFile: (containerId: number | null) => void;
  uploading: boolean;
  /** The last upload's or delete's failure, in words to show inline. */
  error: string | null;
  /** Asks before deleting, as the design's `#shdoc-del-modal` does. */
  askDelete: (document: ShipmentDocument) => void;
  /** The hidden file input and the confirmation — render once, anywhere. */
  elements: ReactNode;
};

type DocumentActionsOptions = {
  upload: (file: File, containerId: number | null) => Promise<string | null>;
  remove: (document: ShipmentDocument) => Promise<string | null>;
};

/**
 * Upload and delete, shared by the Documents tab and the #shdoc drawer so both
 * pick files, report errors and confirm a delete the same way.
 */
export const useDocumentActions = ({ upload, remove }: DocumentActionsOptions): DocumentActions => {
  const inputRef = useRef<HTMLInputElement>(null);
  const target = useRef<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<ShipmentDocument | null>(null);

  const chooseFile = (containerId: number | null) => {
    target.current = containerId;
    inputRef.current?.click();
  };

  const onChosen = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    // Cleared so picking the same file again still fires a change.
    event.target.value = "";
    if (file === undefined) return;

    if (file.size > DOCUMENT_MAX_BYTES) {
      setError(DOCUMENT_TOO_LARGE);
      return;
    }

    setUploading(true);
    setError(null);
    setError(await upload(file, target.current));
    setUploading(false);
  };

  const confirmDelete = async () => {
    const document = deleting;

    setDeleting(null);
    if (document === null) return;

    setError(await remove(document));
  };

  const elements = (
    <>
      <input ref={inputRef} type="file" accept={DOCUMENT_ACCEPT} hidden onChange={(event) => void onChosen(event)} />

      {deleting !== null && (
        <Modal
          title="Delete this document?"
          onClose={() => setDeleting(null)}
          actions={
            <>
              <button type="button" className="pcbtn" onClick={() => setDeleting(null)}>
                Cancel
              </button>
              <button type="button" className="pcbtn pcbtn--danger" onClick={() => void confirmDelete()}>
                Delete
              </button>
            </>
          }
        >
          <p className="pcmodal__t">
            <b>{deleting.name}</b> will be removed from this shipment. This cannot be undone.
          </p>
        </Modal>
      )}
    </>
  );

  return { chooseFile, uploading, error, askDelete: setDeleting, elements };
};
